import { Extension } from '@tiptap/core';

export interface BlockFontSizeOptions {
  types: string[];
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    blockFontSize: {
      setBlockFontSize: (size: string) => ReturnType;
      unsetBlockFontSize: () => ReturnType;
    };
  }
}

// Block-level font size: a `font-size` carried on the paragraph/heading node and
// serialized inline as `style="font-size: …"`. This mirrors Word's model, where
// the paragraph itself has a *base* size (the paragraph-mark font) and runs can
// override individual words on top.
//
// Why a block attribute and not only the `textStyle` span size: the CSS line box
// is sized by the block's own font (its strut), independent of the spans inside
// it. So a `<span>`-only size left the `<p>` at its default size and the line
// spacing never followed the smaller text. A size on the block shrinks the strut
// too, so the default `line-height: normal` tracks it and small text stops
// rendering with oversized spacing — no explicit line-height (and so no backend
// sanitizer change) needed. Per-word overrides keep using the `FontSize` mark.
//
// Mirrors `@tiptap/extension-text-align`: a global attribute on the block types
// plus set/unset commands that `.map().some()` over the types (so the command
// succeeds when the caret is in any one of them).
export const BlockFontSize = Extension.create<BlockFontSizeOptions>({
  name: 'blockFontSize',

  addOptions() {
    return { types: ['paragraph'] };
  },

  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          fontSize: {
            default: null,
            parseHTML: (element) => element.style.fontSize || null,
            renderHTML: (attributes) =>
              attributes.fontSize
                ? { style: `font-size: ${attributes.fontSize}` }
                : {},
          },
        },
      },
    ];
  },

  addCommands() {
    return {
      setBlockFontSize:
        (size) =>
        ({ commands }) =>
          this.options.types
            .map((type) => commands.updateAttributes(type, { fontSize: size }))
            .some((response) => response),
      unsetBlockFontSize:
        () =>
        ({ commands }) =>
          this.options.types
            .map((type) => commands.resetAttributes(type, 'fontSize'))
            .some((response) => response),
    };
  },
});
